import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import {
  TIME_DATE_FORMAT,
  DATE_TIME_FORMAT_NORMALIZED,
} from '../../helpers/dates';
import * as mobileHelper from '../../helpers/mobile';

import withStyles from '@mui/styles/withStyles';
import { FastField, Field } from 'formik';
import { DateTimePicker as MuiDateTimePicker } from '@mui/x-date-pickers';
import MuiTextField from '@mui/material/TextField';
import MuiSwitch from '@mui/material/Switch';
import Grid from '@mui/material/Grid2';
import Button from '@mui/material/Button';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import Fab from '@mui/material/Fab';
import Tooltip from '@mui/material/Tooltip';

export const TextField = withStyles(() => ({
  noMargin: {
    marginTop: 0,
    marginBottom: 0,
  },
}))(props => (
  <FastField {...props}>
    {({ field, form }) => (
      <MuiTextField
        {...field}
        onChange={event => {
          const { value } = event.target;
          if (value === '') {
            form.setFieldValue(field.name, value);
            return;
          }

          const newValue = props.type === 'number' ? Number(value) : value;

          if (!props.allowedInputValue)
            form.setFieldValue(field.name, newValue);
          else if (props.allowedInputValue(newValue))
            form.setFieldValue(field.name, newValue);
          else form.setFieldValue(field.name, field.value);
        }}
        classes={{ root: !props.margin ? props.classes.noMargin : undefined }}
        style={props.style}
        size={props.size || 'small'}
        variant={props.variant || 'standard'}
        multiline={props.multiline}
        rows={props.rows}
        maxRows={props.maxRows}
        label={props.label}
        className={props.className}
        type={props.type}
        inputProps={props.inputProps}
        fullWidth={props.fullWidth}
        autoComplete="off"
        margin={props.margin}
        error={
          !!props.error ||
          (props.highlight && !field.value && field.value !== 0)
        }
        value={!field.value && field.value !== 0 ? props.value : field.value}
        helperText={props.error}
        disabled={props.disabled}
      />
    )}
  </FastField>
));

TextField.defaultProps = {
  value: '',
  highlight: true,
};

export const NumberField = props => <TextField {...props} type="number" />;

// Android Chrome doesn't reliably open the picker for a scripted click();
// showPicker() (Chrome 99+) does. Not used on iOS: Safari's showPicker()
// doesn't open it there.
const openAndroidNativePicker = input => {
  if (input.showPicker) {
    input.showPicker();
    return;
  }
  input.click();
};

export const DateTimePicker = withStyles(() => ({
  nativePicker: {
    overflow: 'hidden',
    height: '1px',
    width: '1px',
    lineHeight: 0,
    padding: 0,
    border: 'none',
    fontSize: 0,
    background: 'none',
    position: 'absolute',
    '&:focus': {
      outline: 'none',
    },
  },
}))(props => {
  const [isOpen, setIsOpen] = useState(false);
  const [initialFocusedDate, setInitialFocusedDate] = useState(new Date());
  useEffect(() => {
    const interval = setInterval(() => {
      const newDate = new Date();
      if (newDate.getMinutes() !== initialFocusedDate.getMinutes())
        setInitialFocusedDate(newDate);
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [initialFocusedDate]);

  const nativePickerRef = useRef();
  const isMobileDevice = mobileHelper.isMobileDevice();
  const isAndroid = mobileHelper.isAndroid();

  return (
    <Field {...props} initialFocusedDate={initialFocusedDate}>
      {({ field, form }) => {
        const setDate = date => {
          form.setFieldValue(field.name, !date ? null : date.valueOf());
        };
        return (
          <>
            <MuiDateTimePicker
              {...field}
              ampm={false}
              open={!isMobileDevice && isOpen}
              initialFocusedDate={initialFocusedDate}
              style={props.style}
              closeOnSelect={true}
              value={field.value ? moment(field.value) : null}
              label={props.label}
              slotProps={{
                field: {
                  variant: 'standard',
                  readOnly: true,
                  InputProps: {
                    value: field.value
                      ? moment(field.value).format(
                          props.format || TIME_DATE_FORMAT
                        )
                      : '',
                    placeholder: '',
                    onClick: () => {
                      if (isAndroid) {
                        // Click fires on every tap; focus only on the first.
                        openAndroidNativePicker(nativePickerRef.current);
                        return;
                      }
                      if (isMobileDevice) {
                        return;
                      }
                      setIsOpen(true);
                    },
                    onFocus: () => {
                      if (!isMobileDevice || isAndroid) {
                        return;
                      }
                      // iOS
                      nativePickerRef.current.click();
                      nativePickerRef.current.focus();
                    },
                  },
                  error: props.error || (props.highlight && !field.value),
                },
                actionBar: {
                  actions: ['clear'],
                },
              }}
              onOpen={() => setIsOpen(true)}
              onClose={() => setIsOpen(false)}
              onChange={isMobileDevice ? undefined : setDate}
            />
            {isMobileDevice && (
              <input
                ref={nativePickerRef}
                className={props.classes.nativePicker}
                type="datetime-local"
                value={
                  field.value
                    ? moment(field.value).format(DATE_TIME_FORMAT_NORMALIZED)
                    : undefined
                }
                onChange={event => {
                  setDate(
                    event.target.value
                      ? moment(event.target.value).toDate()
                      : null
                  );
                }}
              />
            )}
          </>
        );
      }}
    </Field>
  );
});

export const Switch = props => (
  <FastField {...props}>
    {({ field, form }) => (
      <MuiSwitch
        color={props.color}
        checked={field.value}
        onChange={(event, checked) => {
          form.setFieldValue(field.name, checked);
        }}
      />
    )}
  </FastField>
);

export const GridTextField = props => {
  return (
    <Grid size={{ xs: props.xs, md: props.md }} style={props.style}>
      {props.name ? (
        <TextField {...props} style={null} highlight />
      ) : (
        <MuiTextField variant="standard" {...props} style={null} />
      )}
    </Grid>
  );
};

export const GridNumberField = props => (
  <Grid size={{ xs: props.xs, md: props.md }} style={props.style}>
    <NumberField {...props} style={null} highlight />
  </Grid>
);

export const GridContainer = props => (
  <Grid
    container
    size={{ xs: props.xs, md: props.md }}
    spacing={1}
    alignItems="flex-end"
    {...props}
  >
    {props.children}
  </Grid>
);

export const GridDateTimePicker = props => (
  <Grid size={{ xs: props.xs, md: props.md }} style={props.style}>
    <DateTimePicker {...props} style={null} highlight />
  </Grid>
);

export const GridButton = props => (
  <Grid size={{ xs: props.xs, md: props.md }}>
    <Button variant={props.variant ?? 'outlined'} size="small" {...props}>
      {props.label}
    </Button>
  </Grid>
);

export const IconFab = props => (
  <Tooltip
    title={props.tooltip || ''}
    placement={props.tooltipPlacement || 'top'}
    disableFocusListener
  >
    <Fab size="small" {...props}>
      <Icon fontSize="small">{props.icon}</Icon>
    </Fab>
  </Tooltip>
);

export const GridIconButton = props => (
  <Grid size={{ xs: props.xs }} display="flex" justifyContent="flex-end">
    <IconButton variant="outlined" {...props} size="large">
      <Icon fontSize="small">{props.icon}</Icon>
    </IconButton>
  </Grid>
);

export const Placeholder = props => <Grid size={{ xs: props.xs }} />;

export const GridIconFab = props => (
  <Grid size={{ xs: props.xs }} display="flex" justifyContent="flex-end">
    <IconFab {...props} />
  </Grid>
);

export const InlineSpacer = () => (
  <GridContainer style={{ height: '40px' }}>
    <Placeholder xs={12} />
  </GridContainer>
);
